/**
 * AgedLeadsPowerDial.jsx — manual power-dial session UI for the Aged Lead
 * Pool.
 *
 * No AI screener — the agent dials each lead through their browser softphone
 * (@twilio/voice-sdk). The server authorises the dial (TCPA + DNC + lock +
 * 3hr cooldown) before each call; on disposition we advance to the next lead.
 *
 * v1 keeps the softphone integration light: it shows the connecting state +
 * lead info and exposes a disposition selector. The Twilio Device wiring is
 * intentionally minimal — production builds reuse the same Device singleton
 * the AI Dialer Day 4 softphone bridge already creates. We import lazily so
 * this page works in test/demo environments where the SDK is mocked.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from '../lib/api.js';
import { ensureDevice } from '../lib/aiDialerVoice.js';
import * as voice from '../lib/twilioVoice.js';
import TopBar from '../components/TopBar.jsx';
import { Phone, PhoneOff, X, ChevronRight, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const DISPOSITIONS = [
  'Booked', 'Follow-up', 'No Answer', 'Quoted', 'Call Back',
  'Not Interested', 'Sold', 'DNC',
];

function DispositionPanel({ onSelect, disabled }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {DISPOSITIONS.map((d) => (
        <button
          key={d}
          disabled={disabled}
          onClick={() => onSelect(d)}
          className="px-3 py-2 rounded-sm border border-civix-border bg-civix-panel hover:bg-civix-purple/20 hover:border-civix-purple/40 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {d}
        </button>
      ))}
    </div>
  );
}

export default function AgedLeadsPowerDial() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [currentLead, setCurrentLead] = useState(null);
  const [status, setStatus] = useState('idle');   // idle | authorizing | live | post-call
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');

  const advance = useMutation({
    mutationFn: (lastLeadId) => apiPost('/aged-pool/power-dial/next', { sessionId, lastLeadId }),
    onSuccess: (data) => {
      if (!data.lead) {
        toast.success('Pool drained — no more eligible leads.');
        setCurrentLead(null);
        setStatus('idle');
      } else {
        setCurrentLead(data.lead);
        setStatus('idle');
        setNotes('');
      }
    },
    onError: (e) => toast.error(e.message || 'Failed to fetch next lead'),
  });

  const authorize = useMutation({
    mutationFn: (leadId) => apiPost('/aged-pool/power-dial/authorize', { leadId }),
    onMutate: () => { setStatus('authorizing'); setError(null); },
    onSuccess: async () => {
      // Authorize passed all compliance/lock/cooldown gates — now place a REAL
      // call through the shared softphone Device (the same singleton the AI
      // Dialer uses; ensureDevice get-or-inits so we never double-register).
      // The Call mirror lands server-side in POST /api/phone/voice/outbound,
      // keyed on the real Twilio CallSid, tagged via source=aged_pool.
      setStatus('live');
      try {
        await ensureDevice();
        voice.placeCall(currentLead.phone, {
          source: 'aged_pool',
          agedLeadId: String(currentLead._id),
        });
        toast.success('Dialing lead via softphone');
      } catch (e) {
        setStatus('idle');
        setError(e?.message || 'Softphone failed to connect');
        toast.error('Softphone failed to connect');
      }
    },
    onError: (e) => {
      setStatus('idle');
      setError(e.message || 'Authorization failed');
      toast.error(e.message || 'Authorization failed');
    },
  });

  const disposition = useMutation({
    mutationFn: ({ disposition }) => apiPost('/aged-pool/disposition', {
      leadId: currentLead._id, disposition, notes,
    }),
    onSuccess: () => {
      toast.success('Disposition saved');
      advance.mutate(currentLead._id);
    },
    onError: (e) => toast.error(e.message || 'Disposition failed'),
  });

  const stop = useMutation({
    mutationFn: () => apiPost('/aged-pool/power-dial/stop', { sessionId }),
    onSuccess: () => navigate('/aged-leads'),
  });

  useEffect(() => {
    // On mount we fetch the first lead. If the session was created with
    // pre-loaded leads, we still call next() to keep the pop / pop pattern
    // consistent.
    advance.mutate(null);
    return () => { stop.mutate(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Softphone lifecycle → UI. When the live call ends (hangup either side)
    // move to the disposition step; surface hard errors inline.
    const unsub = [
      voice.on('ended', () => setStatus((s) => (s === 'live' ? 'post-call' : s))),
      voice.on('error', (e) => setError(e?.message || 'Call error')),
    ];
    return () => unsub.forEach((u) => u && u());
  }, []);

  return (
    <div className="space-y-4">
      <TopBar title="Power Dial — Aged Pool" subtitle="Manual softphone (no AI)" />

      {currentLead ? (
        <div className="panel p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-white/40 uppercase tracking-wide">Current Lead</div>
              <div className="font-display font-bold text-2xl">
                {currentLead.firstName} {currentLead.lastName}
              </div>
              <div className="text-white/60 text-sm mt-1">
                {currentLead.phone} · {currentLead.state || '—'}
              </div>
              {currentLead.agedPool?.leadType && (
                <div className="text-xs text-white/50 mt-1">
                  Lead Type: {currentLead.agedPool.leadType}
                  {currentLead.agedPool.vendorNotation && <> · Vendor: {currentLead.agedPool.vendorNotation}</>}
                </div>
              )}
            </div>
            <button onClick={() => stop.mutate()} className="btn-ghost flex items-center gap-1 text-sm">
              <X className="w-4 h-4" /> End Session
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Status / call controls */}
          {status === 'idle' && (
            <button
              onClick={() => authorize.mutate(currentLead._id)}
              className="btn-primary w-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" /> Authorize &amp; Dial
            </button>
          )}
          {status === 'authorizing' && (
            <div className="text-center text-white/60 py-4">Authorizing dial…</div>
          )}
          {status === 'live' && (
            <div className="space-y-3">
              <div className="text-center text-emerald-300 font-medium py-2">📞 Live — softphone connected</div>
              <button
                onClick={() => { try { voice.hangUp(); } catch (_) { /* already ended */ } setStatus('post-call'); }}
                className="btn-ghost w-full flex items-center justify-center gap-2"
              >
                <PhoneOff className="w-4 h-4" /> Hang Up &amp; Disposition
              </button>
            </div>
          )}
          {status === 'post-call' && (
            <div className="space-y-3">
              <label className="block text-xs text-white/60 mb-1">Notes (optional)</label>
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-sm bg-civix-dark border border-civix-border text-sm"
                placeholder="What did the lead say?"
              />
              <div>
                <div className="text-xs text-white/60 mb-2">Disposition</div>
                <DispositionPanel
                  disabled={disposition.isPending}
                  onSelect={(d) => disposition.mutate({ disposition: d })}
                />
              </div>
              <button
                onClick={() => advance.mutate(currentLead._id)}
                className="btn-ghost w-full text-sm flex items-center justify-center gap-1"
              >
                Skip to next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="panel p-10 text-center">
          <div className="font-display font-bold text-lg mb-2">Session complete</div>
          <p className="text-white/50 text-sm mb-4">No more eligible leads in the pool.</p>
          <button onClick={() => navigate('/aged-leads')} className="btn-primary">Back to Pool</button>
        </div>
      )}
    </div>
  );
}
